/**
 * Encrypted Device Cache using IndexedDB
 * All device data is encrypted with AES-256-CBC using user-specific keys
 */

import { DeviceInfo } from '../common/enums'
import { encryptionService, EncryptedData } from '../services/encryptionService'
import { cacheSettingsService } from '../services/cache/cacheSettingsService'

const DB_NAME = 'ioag-encrypted-cache'
const DB_VERSION = 1
const STORE_NAME = 'encrypted-devices'

interface EncryptedCacheEntry {
  userHash: string // SHA-256 hash of user email for indexing
  encryptedData: EncryptedData
  timestamp: number
}

interface CacheStats {
  totalRecords: number
  encryptedRecords: number
  userCounts: { [email: string]: number }
  encryptionHealth: {
    isInitialized: boolean
    encryptionServiceAvailable: boolean
    storesAvailable: string[]
    totalEncryptedRecords: number
  }
}

class EncryptedDeviceCache {
  private db: IDBDatabase | null = null
  private initPromise: Promise<void> | null = null

  /**
   * Initialize IndexedDB connection
   */
  private async init(): Promise<void> {
    if (this.db) return
    if (this.initPromise) return this.initPromise

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => {
        console.error('Failed to open encrypted IndexedDB:', request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        // Create object store with userHash as key
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'userHash' })
          store.createIndex('timestamp', 'timestamp', { unique: false })
        }
      }
    })

    return this.initPromise
  }

  /**
   * Get cached devices for a specific email (decrypted)
   */
  async getDevices(email: string): Promise<DeviceInfo[] | null> {
    try {
      await this.init()
      if (!this.db) return null

      const userHash = encryptionService.hashUserIdentifier(email)

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readonly')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.get(userHash)

        request.onsuccess = () => {
          const result = request.result as EncryptedCacheEntry | undefined
          if (result) {
            try {
              // Decrypt the data (or parse plain JSON if encryption is disabled)
              let decryptedData: { devices: DeviceInfo[] }

              if (cacheSettingsService.isEncryptionEnabled()) {
                decryptedData = encryptionService.decrypt<{ devices: DeviceInfo[] }>(
                  result.encryptedData,
                  email
                )
              } else {
                decryptedData = JSON.parse(result.encryptedData.ciphertext)
              }

              resolve(decryptedData.devices)
            } catch (error) {
              console.error('Failed to decrypt cached devices:', error)
              // If decryption fails, clear the corrupted data
              this.clearDevices(email)
              resolve(null)
            }
          } else {
            resolve(null)
          }
        }

        request.onerror = () => {
          console.error('Failed to get encrypted cached devices:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error accessing encrypted device cache:', error)
      return null
    }
  }

  /**
   * Cache devices for a specific email (encrypted)
   */
  async setDevices(email: string, devices: DeviceInfo[]): Promise<void> {
    try {
      await this.init()
      if (!this.db) return

      const userHash = encryptionService.hashUserIdentifier(email)

      // Encrypt the device data (or store as plain JSON if encryption is disabled)
      const dataToEncrypt = { devices, email }
      let encryptedData: EncryptedData

      if (cacheSettingsService.isEncryptionEnabled()) {
        encryptedData = encryptionService.encrypt(dataToEncrypt, email)
      } else {
        // Store as plain JSON in ciphertext field when encryption is disabled
        encryptedData = {
          ciphertext: JSON.stringify(dataToEncrypt),
          iv: '',
          salt: ''
        }
      }

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite')
        const store = transaction.objectStore(STORE_NAME)

        const entry: EncryptedCacheEntry = {
          userHash,
          encryptedData,
          timestamp: Date.now()
        }

        const request = store.put(entry)

        request.onsuccess = () => {
          resolve()
        }

        request.onerror = () => {
          console.error('Failed to cache encrypted devices:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error caching encrypted devices:', error)
    }
  }

  /**
   * Clear cached devices for a specific email
   */
  async clearDevices(email: string): Promise<void> {
    try {
      await this.init()
      if (!this.db) return

      const userHash = encryptionService.hashUserIdentifier(email)

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.delete(userHash)

        request.onsuccess = () => {
          resolve()
        }

        request.onerror = () => {
          console.error('Failed to clear encrypted cached devices:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error clearing encrypted device cache:', error)
    }
  }

  /**
   * Clear all cached devices
   */
  async clearAll(): Promise<void> {
    try {
      await this.init()
      if (!this.db) return

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.clear()

        request.onsuccess = () => {
          resolve()
        }

        request.onerror = () => {
          console.error('Failed to clear all encrypted cached devices:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error clearing all encrypted device cache:', error)
    }
  }

  /**
   * Get cache metadata for a specific email
   */
  async getCacheMetadata(email: string): Promise<{ timestamp: number } | null> {
    try {
      await this.init()
      if (!this.db) return null

      const userHash = encryptionService.hashUserIdentifier(email)

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readonly')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.get(userHash)

        request.onsuccess = () => {
          const result = request.result as EncryptedCacheEntry | undefined
          if (result) {
            resolve({ timestamp: result.timestamp })
          } else {
            resolve(null)
          }
        }

        request.onerror = () => {
          console.error('Failed to get cache metadata:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error getting cache metadata:', error)
      return null
    }
  }

  /**
   * Get real cache statistics
   */
  async getCacheStats(): Promise<CacheStats> {
    try {
      await this.init()
      if (!this.db) {
        return this.getEmptyStats()
      }

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readonly')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.getAll()

        request.onsuccess = () => {
          const entries = request.result as EncryptedCacheEntry[]

          const stats: CacheStats = {
            totalRecords: entries.length,
            encryptedRecords: entries.length,
            userCounts: {},
            encryptionHealth: {
              isInitialized: true,
              encryptionServiceAvailable: encryptionService.isAvailable(),
              storesAvailable: [STORE_NAME],
              totalEncryptedRecords: entries.length
            }
          }

          // Note: We can't determine exact user counts without decryption
          // which would require all user emails. This is a privacy feature.
          resolve(stats)
        }

        request.onerror = () => {
          console.error('Failed to get cache stats:', request.error)
          resolve(this.getEmptyStats())
        }
      })
    } catch (error) {
      console.error('Error getting cache stats:', error)
      return this.getEmptyStats()
    }
  }

  /**
   * Get user-specific cache stats (requires email for decryption)
   */
  async getUserCacheStats(email: string): Promise<{
    hasCache: boolean
    deviceCount: number
    lastUpdated: number | null
  }> {
    try {
      const devices = await this.getDevices(email)
      const metadata = await this.getCacheMetadata(email)

      return {
        hasCache: devices !== null,
        deviceCount: devices?.length || 0,
        lastUpdated: metadata?.timestamp || null
      }
    } catch (error) {
      console.error('Error getting user cache stats:', error)
      return {
        hasCache: false,
        deviceCount: 0,
        lastUpdated: null
      }
    }
  }

  /**
   * Migrate data from unencrypted cache
   */
  async migrateFromUnencryptedCache(email: string, devices: DeviceInfo[]): Promise<void> {
    if (!devices || devices.length === 0) return

    await this.setDevices(email, devices)
  }

  /**
   * Check if migration is needed
   */
  async needsMigration(): Promise<boolean> {
    try {
      // Check if old unencrypted database exists
      const databases = await indexedDB.databases()
      return databases.some(db => db.name === 'ioag-device-cache')
    } catch {
      // Fallback for browsers that don't support databases()
      return true // Assume migration might be needed
    }
  }

  private getEmptyStats(): CacheStats {
    return {
      totalRecords: 0,
      encryptedRecords: 0,
      userCounts: {},
      encryptionHealth: {
        isInitialized: false,
        encryptionServiceAvailable: encryptionService.isAvailable(),
        storesAvailable: [],
        totalEncryptedRecords: 0
      }
    }
  }
}

// Export singleton instance
export const encryptedDeviceCache = new EncryptedDeviceCache()
export type { CacheStats }